import { createContext, useContext, useState, useCallback } from "react";

export interface FeatureFlags {
  inventory: boolean;
}

export interface FeatureFlagsContextType {
  flags: FeatureFlags;
  setFlag: (key: keyof FeatureFlags, value: boolean) => void;
}

export const FEATURE_FLAG_DEFAULTS: FeatureFlags = {
  inventory: false,
};

export const FeatureFlagsContext = createContext<FeatureFlagsContextType | null>(null);

export function useFeatureFlags() {
  const context = useContext(FeatureFlagsContext);
  if (!context) {
    throw new Error("useFeatureFlags must be used within a FeatureFlagsProvider");
  }
  return context;
}

export function FeatureFlagsProvider({ children }: { children: React.ReactNode }) {
  const [flags, setFlags] = useState<FeatureFlags>(() => {
    const stored = localStorage.getItem("cardvault-feature-flags");
    if (stored) {
      return { ...FEATURE_FLAG_DEFAULTS, ...JSON.parse(stored) };
    }
    return FEATURE_FLAG_DEFAULTS;
  });

  const setFlag = useCallback((key: keyof FeatureFlags, value: boolean) => {
    setFlags((prev) => {
      const next = { ...prev, [key]: value };
      localStorage.setItem("cardvault-feature-flags", JSON.stringify(next));
      return next;
    });
  }, []);

  return (
    <FeatureFlagsContext.Provider value={{ flags, setFlag }}>
      {children}
    </FeatureFlagsContext.Provider>
  );
}
